import React, { useState, useEffect, useRef } from 'react';
import { Box, Snackbar, Typography, Stack, Paper, Container, TextField } from '@mui/material';
import { useLocation, useNavigate } from 'react-router';
import LoadingButton from '@mui/lab/LoadingButton';
import MuiAlert, { AlertProps } from '@mui/material/Alert';
import RefreshIcon from '@mui/icons-material/Refresh';
import axios from 'axios';
import { EurofinsLogo } from 'components/svg/EurofinsLogo';
import { ElvisConnect } from 'components/svg/ElvisConnect';
import { getErrorMessage } from 'helpers/apiErrorEvent';

const OTP_LENGTH = 6;
const RESEND_SECONDS = 299;
const emptyOtp = () => Array(OTP_LENGTH).fill('');

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

export const VerificationOtp = () => {
    let navigate = useNavigate();
    const [otp, setOtp] = useState<string[]>(emptyOtp);
    const [loading, setLoading] = useState(false);
    const [resendLoading, setResendLoading] = useState(false);
    const [error, setError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [resentSuccessMessage, setResentSuccessMessage] = useState(false);
    const [timer, setTimer] = useState(RESEND_SECONDS);
    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
    const location: any = useLocation();
    const queryParam = new URLSearchParams(location.search);
    const email = queryParam.get('emailId');
    const emailId = location?.state?.emailId;
    const accessTokenValue = location?.state?.accessTokenValue;
    const displayEmail = emailId || email;
    const isOtpComplete = otp.every((digit) => digit !== '');

    useEffect(() => {
        if (timer <= 0) return;
        const timeout = setTimeout(() => setTimer(timer - 1), 1000);
        return () => clearTimeout(timeout);
    }, [timer]);

    const resetOtp = () => {
        setOtp(emptyOtp());
        inputRefs.current[0]?.focus();
    };

    const handleOtpChange = (index: number, value: string) => {
        if (!/^\d*$/.test(value)) return;
        const digit = value.slice(-1);
        const newOtp = [...otp];
        newOtp[index] = digit;
        setOtp(newOtp);
        if (digit && index < OTP_LENGTH - 1) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
    };

    const handlePaste = (e: React.ClipboardEvent) => {
        e.preventDefault();
        const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH);
        if (!pasted) return;
        const newOtp = emptyOtp();
        pasted.split('').forEach((digit, i) => { newOtp[i] = digit; });
        setOtp(newOtp);
        inputRefs.current[Math.min(pasted.length, OTP_LENGTH - 1)]?.focus();
    };

    const verify = (e: any) => {
        e.preventDefault();
        if (!isOtpComplete || loading) return;

        setLoading(true);

        const params = {
            emailId: emailId ? emailId : email,
            Otp: otp.join('')
        }

        axios.post(`${process.env.REACT_APP_BASE_URL}/validateOTP`, params,
            {
                responseType: 'json',
                headers: {
                    'Authorization': accessTokenValue ? `Bearer ${accessTokenValue}` : '',
                    "content-type": "application/json"
                }
            }
        ).then((res: any) => {
            if (res?.status === 200) {
                localStorage.setItem('token', res.data.token);
                localStorage.setItem('refreshToken', res.data.refreshToken);
                let redirectStatus = res.data.token ? JSON.parse(atob(res.data.token.split('.')[1])) : null;
                if (redirectStatus?.ValidOTP === 'True') {
                    if (redirectStatus?.NeedsPasswordReset === 'True') {
                        navigate(`/resetPassword`);
                    } else if (redirectStatus && (redirectStatus?.UserType === 'Internal')) {
                        navigate(`/home/viewQuote`);
                    } else {
                        navigate(`/home`);
                    }
                }
            }
        }).finally(() => {
            setLoading(false);
        }).catch((error: any) => {
            setError(true);
            setErrorMessage(getErrorMessage(error, 'Please provide the correct verification code!'));
            resetOtp();
        });
    }

    const resendOtp = () => {
        setResendLoading(true);
        axios.post(`${process.env.REACT_APP_BASE_URL}/ResendOTP`, '',
            {
                responseType: 'json',
                headers: {
                    'Authorization': accessTokenValue ? `Bearer ${accessTokenValue}` : '',
                    "content-type": "application/json"
                }
            }
        ).then((res: any) => {
            if (res.status === 200) {
                setTimer(RESEND_SECONDS);
                setResentSuccessMessage(true);
                resetOtp();
            }
            else {
                setError(true);
                setErrorMessage(getErrorMessage({ response: res }, 'Failed to resend OTP. Please try again.'));
            }
        }).catch((error: any) => {
            setError(true);
            setErrorMessage(getErrorMessage(error, 'Failed to resend OTP. Please try again.'));
        }).finally(() => {
            setResendLoading(false);
        });
    }

    const otpBoxSize = { xs: 42, sm: 50, md: 56 };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                background: 'linear-gradient(to right, #003883, #ee7d11)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                py: 4
            }}
        >
            <Container maxWidth="sm">
                <Paper
                    component="form"
                    onSubmit={verify}
                    elevation={0}
                    sx={{
                        p: { xs: 3, md: 5 },
                        borderRadius: 3,
                        background: '#ffffff',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06), 0 8px 24px rgba(0, 0, 0, 0.08)',
                        border: '1px solid #e8e8e8',
                        position: 'relative',
                        overflow: 'hidden',
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            height: '5px',
                            background: 'linear-gradient(90deg, #003883 0%, #0056b3 100%)',
                        }
                    }}
                >
                    <Box sx={{ mb: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                        <Box sx={{ mb: 1 }}><EurofinsLogo /></Box>
                        <Box sx={{ mb: 3 }}><ElvisConnect /></Box>
                        <Typography
                            variant="h4"
                            component="h1"
                            data-testid="otp-title"
                            sx={{ fontWeight: 600, mb: 2, fontSize: '18px', color: 'secondary.main' }}
                        >
                            Verify Your Email
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 1, fontSize: '12px' }}>
                            Please enter the One-Time Password shared with your email ID
                        </Typography>
                        {displayEmail && (
                            <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '12px', color: 'secondary.main' }}>
                                {displayEmail}
                            </Typography>
                        )}
                    </Box>

                    <Box sx={{ mb: 4, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            Enter the verification code below:
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: { xs: 0.5, sm: 1, md: 1.5 }, mb: 3, px: 2 }}>
                            {otp.map((digit, index) => (
                                <TextField
                                    key={index}
                                    inputRef={(el) => (inputRefs.current[index] = el)}
                                    value={digit}
                                    autoFocus={index === 0}
                                    onChange={(e) => handleOtpChange(index, e.target.value)}
                                    onKeyDown={(e) => handleKeyDown(index, e)}
                                    onPaste={handlePaste}
                                    variant="outlined"
                                    inputProps={{
                                        maxLength: 1,
                                        inputMode: 'numeric',
                                        'aria-label': `OTP digit ${index + 1}`,
                                        'data-testid': `otp-input-${index}`,
                                    }}
                                    sx={{
                                        width: otpBoxSize,
                                        '& .MuiOutlinedInput-root': { height: otpBoxSize, p: 0 },
                                        '& .MuiOutlinedInput-input': {
                                            height: '100%',
                                            padding: '0 !important',
                                            textAlign: 'center',
                                            fontSize: '22px',
                                            fontWeight: 600,
                                            boxSizing: 'border-box',
                                        },
                                    }}
                                />
                            ))}
                        </Box>
                    </Box>

                    <Box sx={{ mb: 3, textAlign: 'center' }}>
                        <LoadingButton
                            type="submit"
                            variant="contained"
                            size="large"
                            loading={loading}
                            disabled={!isOtpComplete || loading}
                            data-testid="confirm-otp-button"
                            sx={{ fontWeight: 600, textTransform: 'none', fontSize: '12px', width: 'auto', px: 4, minWidth: 0 }}
                        >
                            {loading ? 'Verifying...' : 'Confirm OTP'}
                        </LoadingButton>
                    </Box>

                    <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Didn't receive the code?
                        </Typography>
                        {timer <= 0 ? (
                            <LoadingButton
                                variant="outlined"
                                onClick={resendOtp}
                                loading={resendLoading}
                                startIcon={<RefreshIcon />}
                                data-testid="resend-otp-button"
                                sx={{ textTransform: 'none', fontWeight: 600 }}
                            >
                                {resendLoading ? 'Resending...' : 'Resend OTP'}
                            </LoadingButton>
                        ) : (
                            <Typography variant="body2" color="secondary.main">
                                Resend OTP in {Math.floor(timer / 60)}:{(timer % 60).toString().padStart(2, '0')} seconds
                            </Typography>
                        )}
                    </Box>
                </Paper>
            </Container>
            <Stack>
                <Snackbar open={error} autoHideDuration={6000} onClose={() => setError(false)}>
                    <Alert severity="error" onClose={() => setError(false)}>
                        {errorMessage || 'Please provide the correct verification code!'}
                    </Alert>
                </Snackbar>
                <Snackbar open={resentSuccessMessage} autoHideDuration={6000} onClose={() => setResentSuccessMessage(false)}>
                    <Alert severity="success" onClose={() => setResentSuccessMessage(false)}>
                        Resent OTP successfully!
                    </Alert>
                </Snackbar>
            </Stack>
        </Box>
    )
}
